import { useGetCinemaList } from '@/logic/services/cinemas';
import type { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { handleError, is500 } from '@/logic/shared';
import { useAuth, useCinemas, useDialog, useMoviesType } from '@/logic/state';
import { useGetBanners, useGetInfiniteMovies } from '@/logic/services';

import { AxiosError } from 'axios';
import { Box, Container, useTheme } from '@mui/material';

import { MovieTypesEnum } from '@/constants/movie-types-enum';

import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { buildOrganizationSchemaComponent } from '@/logic/helpers/google-structured-data';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import shallow from 'zustand/shallow';
import { useCity } from '@/logic/hooks';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import { useIntl } from 'react-intl';
import { HomeSlider } from '@/components/home-slider/home-slider';
import { MoviesSection } from '@/components/home/movies-section';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';
import WalletCashbackInfoBadge from '@/features/wallet/components/wallet-access/wallet-cashback-info-badge';
import { UserType, useUserTypes } from '@/logic/hooks/use-user-types';
import { PATHS } from '@/constants/routes';
import { useRouter } from 'next/router';
import { MyBalanceBadge } from '@/features/wallet/components/wallet-access/my-balance-badge';

const paginationShowing = {
  limit: 4,
  page: 1,
};

interface filmsParams {
  cityId?: number;
  limit: number;
  page: number;
  type: MovieTypesEnum;
  cinemaIds: number[];
}

// The hero images have no hand-written preloads. The first desktop cover
// (BackgroundsSliderItem) and the first mobile poster (PostersSliderItem) are
// `priority`, and next/legacy/image puts a preload with the image's own srcset
// and `sizes` into the server-rendered <head>. A hand-written preload naming a
// different URL from the one the <img> picks makes the browser fetch both.
const Home: NextPage<{
  serverCityId: number;
  serverMoviesType: MovieTypesEnum;
  path: string;
}> = ({ serverCityId, serverMoviesType, path }) => {
  const intl = useIntl();
  const { cityId: browserCityId } = useCity();
  const [browserMoviesType, setBrowserMoviesType] = useMoviesType(
    s => [s.moviesType, s.setMoviesType],
    shallow
  );
  const theme = useTheme();
  const user = useAuth(s => s.user, shallow);
  const setDialog = useDialog(s => s.setDialog, shallow);
  const { userType } = useUserTypes();

  const cityId = browserCityId || serverCityId;
  const moviesType = browserMoviesType || serverMoviesType;
  const router = useRouter();

  const selectedCinemas = useCinemas(s => s.selectedCinemas, shallow);

  const { data: seoDataList } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(seoDataList?.pageMetaData, SeoPagesEnum.home);

  const { data: banners } = useGetBanners();
  // const setUnreadNotifications = useUnreadNotifications(
  //   s => s.setUnreadNotifications,
  //   shallow
  // );

  // useGetUnreadNotificationsQuery(
  //   {},
  //   {
  //     onSuccess: data => {
  //       setUnreadNotifications(data.count);
  //     },
  //     enabled: !!user?.token,
  //   }
  // );

  const { data: cinemasList } = useGetCinemaList({
    params: { ...(cityId && { cityId: cityId }) },
  });

  const [filmsParams, setFilmsParams] = useState<filmsParams>({
    ...(serverCityId && { cityId: serverCityId }),
    limit: paginationShowing.limit,
    page: paginationShowing.page,
    type: serverMoviesType,
    cinemaIds: [],
  });

  useEffect(() => {
    const tempParams = {
      ...(cityId && { cityId: cityId }),
      limit: paginationShowing.limit,
      page: paginationShowing.page,
      type: moviesType,
      cinemaIds: selectedCinemas.filter(cinemaId => cinemaId !== 0),
    };
    setFilmsParams(tempParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationShowing.limit, paginationShowing.page, moviesType, selectedCinemas]);

  useEffect(() => {
    if (!browserMoviesType) {
      setBrowserMoviesType(MovieTypesEnum.SHOW_NOW);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Always enabled: the initial filmsParams (cinemaIds: []) matches what
  // getServerSideProps prefetched, so this reads that dehydrated cache and
  // paints immediately instead of waiting for selectedCinemas to populate.
  // The effect above still refetches with the real filter once it does.
  const { data, fetchNextPage, hasNextPage, isFetching } = useGetInfiniteMovies({
    params: filmsParams,
  });

  const movies = useMemo(
    () => (data?.pages ? data?.pages?.flatMap(group => group.data) : []),
    [data]
  );

  const handleBadgeClick = (): void => {
    if (!user) {
      setDialog(() => ({
        wrapper: true,
        isPhoneNumberForm: true,
      }));
      return;
    }
    if (
      userType === UserType.Completed ||
      userType === UserType.Verified ||
      userType === UserType.NonVerified
    ) {
      router.push(PATHS.USER_AREA);
      return;
    }
    if (userType === UserType.NonCompleted) {
      setDialog(() => ({
        wrapper: true,
        isCompleteProfileForm: true,
        completeProfileFormPayload: {
          hideMaybeLaterButton: true,
        },
      }));
      return;
    }
  };

  return (
    <Box>
      <Seo
        title={
          seoPage?.titleTag ||
          intl.formatMessage({
            id: 'home_page',
          })
        }
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={seoPage?.descriptionTag}
        components={buildOrganizationSchemaComponent}
        hasFollow={seoPage?.hasFollow}
        hasIndex={seoPage?.hasIndex}
      />
      {banners?.banner.length && <HomeSlider banners={banners.banner} />}

      {!user ? (
        <WalletCashbackInfoBadge
          text='wallet.user_is_guest'
          customImg='/icons/profile/wallet-cashback-login-icon.svg'
          onBadgeClick={handleBadgeClick}
        />
      ) : (
        <> </>
      )}
      {userType === UserType.NonCompleted ? (
        <WalletCashbackInfoBadge
          text='wallet.user_not_completed_profile.side_menu'
          customImg='/icons/profile/wallet-cashback-profile-icon.svg'
          onBadgeClick={handleBadgeClick}
        />
      ) : (
        <> </>
      )}
      {userType === UserType.Completed ||
      userType === UserType.Verified ||
      userType === UserType.NonVerified ? (
        <Container
          component='section'
          sx={{
            maxWidth: '1784px !important',
            paddingX: {
              mobile: '16px !important',
              largeTablet: '30px !important',
              largeDesktop: '60px !important',
            },
            marginBlockEnd: 10,
          }}
        >
          <MyBalanceBadge
            customArrowButtonSx={{
              backgroundColor: 'transparent',
              color: theme.palette.typography.color.secondary,
            }}
          />
        </Container>
      ) : (
        <></>
      )}
      <MoviesSection
        moviesList={movies}
        mainCinemasList={cinemasList}
        hasMore={hasNextPage}
        fetchMovies={fetchNextPage}
        isFetching={isFetching}
        selectedMoviesType={moviesType}
      />
    </Box>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { locale, defaultLocale } = context;
  const headers = {
    ...getLangHeaders(context.locale),
  };

  const cityId = Number(context.req.cookies.city);
  const moviesType = context.req.cookies.moviesType
    ? context.req.cookies.moviesType
    : MovieTypesEnum.SHOW_NOW;

  // Next defaults SSR responses to `no-store`, so every visitor pays the full
  // ~900ms of the fetches below even when the response is identical. Only the
  // cookie-less response is shareable — it carries no city, so there is nothing
  // personalized to leak into a shared cache — and that is the slow path that
  // matters: first-time visitors and crawlers.
  // ponytail: cityId-cookie requests stay uncached. Widen this once CloudFront
  // includes the `city` cookie in its cache key.
  //
  // The `moviesType` cookie is part of that same condition: it switches the
  // grid between SHOW_NOW and COMING_SOON, so those two responses differ while
  // the URL does not. Marking either of them `public` would let a shared cache
  // hand one visitor's variant to another. Nothing stores this today
  // (CloudFront serves HTML with Managed-CachingDisabled), which is exactly why
  // it has to be right before edge caching is switched on.
  if (!cityId && !context.req.cookies.moviesType) {
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=60, stale-while-revalidate=300'
    );
  }

  const queryClient = new QueryClient();

  // The SEO meta, banners, cinema list and translations are independent of one
  // another, so they are fetched concurrently. Only the films query below has a
  // real dependency — it needs the cinema ids — so it stays sequential.
  //
  // These four used to be awaited one after another, which made the document
  // response the sum of four round trips (~1.7s of the measured 2,559ms TTFB)
  // rather than the slowest single one.
  //
  // `allSettled` rather than `all`: the original wrapped each call in its own
  // try/catch and only bailed out on a 500, letting any other failure fall
  // through with that query's data simply absent from the dehydrated cache.
  // `all` would reject the whole batch on the first error and lose that.
  const [seoMetaResult, bannersResult, cinemasResult, intlMessagesResult] =
    await Promise.allSettled([
      useGetPagesSeoMeta.fetchQuery(queryClient, {
        headers,
      }),
      useGetBanners.fetchQuery(queryClient, {
        headers,
      }),
      useGetCinemaList.fetchQuery(queryClient, {
        params: { ...(cityId && { cityId: cityId }) },
        headers,
      }),
      loadIntlMessages(locale as string, defaultLocale),
      // The client's useGetInfiniteMovies call filters by the visitor's
      // selectedCinemas, which lives in a client-only zustand store seeded
      // from cinemasList after hydration — genuinely not knowable here without
      // a second, sequential round trip. Prefetching with cinemaIds: [] (the
      // same "no filter" shape the movie-detail page already prefetches with,
      // see src/pages/movies/[slug]/index.tsx) is a deliberate mismatch, not a
      // bug: it warms the cache with an unfiltered first page so the grid
      // paints from SSR HTML instead of an empty loading state, and the client
      // hook still refetches with the real filter once selectedCinemas
      // resolves — this is a paint-time warm start, not a full cache hit.
      useGetInfiniteMovies.fetchQuery(queryClient, {
        params: {
          ...(cityId && { cityId: cityId }),
          limit: paginationShowing.limit,
          page: paginationShowing.page,
          type: moviesType,
          cinemaIds: [],
        },
        headers,
      }),
    ]);

  // Checked in the original declaration order, not completion order, so which
  // error page a visitor gets does not depend on which request happened to be
  // slowest.
  for (const result of [seoMetaResult, bannersResult, cinemasResult]) {
    if (result.status === 'rejected' && is500(result.reason)) {
      return handleError(result.reason as AxiosError);
    }
  }

  // Translations were previously awaited unguarded, so a failure propagated out
  // of getServerSideProps. Keep that — the page is not renderable without them.
  if (intlMessagesResult.status === 'rejected') {
    throw intlMessagesResult.reason;
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      serverCityId: cityId,
      serverMoviesType: moviesType,
      path: context.resolvedUrl,
      intlMessages: intlMessagesResult.value,
    },
  };
};

export default Home;
